/**
 * Panel lateral "Crear tarea" estilo HubSpot (reutilizable).
 * Se usa tanto en la pestaña Tareas del negocio (card pre-asociada) como en el
 * submenú global de Tareas (se elige el negocio a asociar).
 *
 * Al crear, opcionalmente genera un ticket en Mesa de Ayuda (casilla activada
 * por defecto) referenciando el pipeline/negocio.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { CheckSquare, Phone, Mail, Search, X, Loader2, Ticket, Paperclip, UploadCloud, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS = [
  { value: "por_hacer", label: "Por hacer", icon: CheckSquare },
  { value: "llamada", label: "Llamada", icon: Phone },
  { value: "correo", label: "Correo", icon: Mail },
];
const ESTATUS = [
  { value: "ninguno", label: "Ninguno" },
  { value: "no_iniciada", label: "No iniciada" },
  { value: "en_progreso", label: "En progreso" },
  { value: "esperando", label: "Esperando" },
  { value: "aplazada", label: "Aplazada" },
  { value: "completada", label: "Completada" },
];
const RECORDATORIOS = [
  { value: "sin_recordatorio", label: "Sin recordatorio" },
  { value: "1h", label: "1 hora antes" },
  { value: "1d", label: "1 día antes" },
  { value: "1w", label: "1 semana antes" },
];

const EMPTY = {
  title: "", task_type: "por_hacer", priority: "ninguna", priority_id: "", task_status: "ninguno", task_type_id: "",
  assigned_to: "", due_date: "", reminder: "sin_recordatorio",
  repeat_enabled: false, repeat_interval: 1, repeat_unit: "dia",
  notes: "", create_ticket: true,
};

export default function CreateTaskPanel({ open, onOpenChange, cardId, cardLabel, users = [], onCreated, pipelineId = "" }) {
  const { getAuthHeaders, user } = useAuth();
  const [form, setForm] = useState({ ...EMPTY, assigned_to: user?.id || "" });
  const [assocCard, setAssocCard] = useState(cardId ? { id: cardId, title: cardLabel } : null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [prioridades, setPrioridades] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!pipelineId) { setTaskTypes([]); return; }
    fetch(`${API_URL}/api/pipelines/${pipelineId}/task-types`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setTaskTypes(d.items || []))
      .catch(() => {});
  }, [pipelineId, getAuthHeaders]);

  useEffect(() => {
    fetch(`${API_URL}/api/tickets/prioridades`, getAuthHeaders())
      .then((r) => r.json())
      .then((d) => setPrioridades(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [getAuthHeaders]);

  const selPrio = prioridades.find((p) => p.id === form.priority_id);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const MAX_SIZE = 10 * 1024 * 1024;
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const valid = [];
    for (const f of incoming) {
      if (f.size > MAX_SIZE) { toast.error(`"${f.name}" excede 10 MB`); continue; }
      valid.push(f);
    }
    if (valid.length) setFiles((prev) => [...prev, ...valid]);
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
  const readB64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, assigned_to: user?.id || "" });
      setAssocCard(cardId ? { id: cardId, title: cardLabel } : null);
      setSearch(""); setResults([]); setFiles([]);
    }
  }, [open, cardId, cardLabel, user]);

  const doSearch = useCallback((q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/pipelines/cards-search?q=${encodeURIComponent(q)}`, getAuthHeaders());
        const d = await r.json();
        setResults(d.items || []);
        setShowResults(true);
      } catch { setResults([]); }
    }, 300);
  }, [getAuthHeaders]);

  const submit = async (addAnother) => {
    if (!form.title.trim()) { toast.error("Indique el título de la tarea"); return; }
    if (!assocCard?.id) { toast.error("Asocie la tarea con un negocio"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        title: form.title.trim(),
        assigned_to: form.assigned_to || null,
        status: form.task_status === "completada" ? "completada" : "pendiente",
        description: form.notes || "",
      };
      const r = await fetch(`${API_URL}/api/pipelines/cards/${assocCard.id}/tasks`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      const created = await r.json();

      // Subir adjuntos (uno por request para evitar límites del proxy).
      if (files.length && created.id) {
        let okCount = 0;
        for (const f of files) {
          try {
            const b64 = await readB64(f);
            const ar = await fetch(`${API_URL}/api/pipelines/tasks/${created.id}/attachments`, {
              method: "POST",
              headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
              body: JSON.stringify({ name: f.name, size: f.size, content_type: f.type || "application/octet-stream", data_b64: b64 }),
            });
            if (ar.ok) okCount += 1;
          } catch { /* continúa con el resto */ }
        }
        if (okCount < files.length) toast.warning(`${okCount}/${files.length} adjunto(s) subido(s)`);
      }

      if (created.helpdesk_ticket_numero) {
        toast.success(`Tarea creada · Ticket #${created.helpdesk_ticket_numero} en Mesa de Ayuda`);
      } else {
        toast.success("Tarea creada");
      }
      onCreated && onCreated(created);
      if (addAnother) {
        setForm({ ...EMPTY, assigned_to: user?.id || "" });
        setFiles([]);
      } else {
        onOpenChange(false);
      }
    } catch {
      toast.error("No se pudo crear la tarea");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0" data-testid="create-task-panel">
        <SheetHeader className="px-5 py-4 border-b sticky top-0 bg-white z-10">
          <SheetTitle className="text-lg flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-[#193966]" /> Crear tarea
          </SheetTitle>
        </SheetHeader>

        <div className="px-5 py-4 space-y-4">
          <div>
            <Label className="text-xs font-medium text-slate-600">Título de la tarea</Label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Ej: Configurar método de pago"
              data-testid="task-title-input"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Tipo de tarea</Label>
              <Select value={form.task_type} onValueChange={(v) => set("task_type", v)}>
                <SelectTrigger className="mt-1" data-testid="task-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Prioridad (SLA Mesa de Ayuda)</Label>
              <Select
                value={form.priority_id || "none"}
                onValueChange={(v) => {
                  if (v === "none") { setForm((f) => ({ ...f, priority_id: "", priority: "ninguna" })); return; }
                  const p = prioridades.find((x) => x.id === v);
                  setForm((f) => ({ ...f, priority_id: v, priority: p?.name || "" }));
                }}
              >
                <SelectTrigger className="mt-1" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna (sin SLA)</SelectItem>
                  {prioridades.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color || "#64748B" }} />
                        {p.name} · SLA {p.sla_hours}h
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tipo de tarea con SLA propio del pipeline */}
          {taskTypes.length > 0 && (
            <div>
              <Label className="text-xs font-medium text-slate-600">Tipo de tarea (SLA del pipeline)</Label>
              <Select
                value={form.task_type_id || "none"}
                onValueChange={(v) => set("task_type_id", v === "none" ? "" : v)}
              >
                <SelectTrigger className="mt-1" data-testid="task-type-sla-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin tipo</SelectItem>
                  {taskTypes.map((tt) => (
                    <SelectItem key={tt.id} value={tt.id}>
                      {tt.name}{tt.sla_hours ? ` · SLA ${tt.sla_hours}h` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => { const tt = taskTypes.find((x) => x.id === form.task_type_id); return tt?.sla_hours ? (
                <p className="text-[11px] text-slate-400 mt-1">La fecha límite se calculará automáticamente: {tt.sla_hours}h desde la creación.</p>
              ) : null; })()}
            </div>
          )}

          {/* Asociar con negocio */}
          <div>
            <Label className="text-xs font-medium text-slate-600">Asociar con negocio</Label>
            {assocCard?.id && cardId ? (
              <div className="mt-1 flex items-center gap-2 p-2 rounded border bg-slate-50 text-sm">
                <Badge variant="secondary">{assocCard.title || "Negocio"}</Badge>
              </div>
            ) : assocCard?.id ? (
              <div className="mt-1 flex items-center gap-2 p-2 rounded border bg-slate-50 text-sm" data-testid="task-assoc-selected">
                <span className="flex-1 truncate">{assocCard.title}</span>
                <button onClick={() => setAssocCard(null)} className="text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="relative mt-1">
                <div className="flex items-center gap-2 border rounded px-2">
                  <Search className="w-4 h-4 text-slate-400" />
                  <input
                    className="flex-1 h-9 text-sm outline-none bg-transparent"
                    placeholder="Buscar negocio…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); doSearch(e.target.value); }}
                    onFocus={() => { doSearch(search); }}
                    data-testid="task-assoc-search"
                  />
                </div>
                {showResults && results.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                    {results.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { setAssocCard(c); setShowResults(false); }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                        data-testid={`task-assoc-opt-${c.id}`}
                      >
                        {c.title}
                        <span className="text-[10px] text-slate-400 ml-2">{c.pipeline_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Asignado a</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => set("assigned_to", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="task-assigned-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nadie —</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Estatus</Label>
              <Select value={form.task_status} onValueChange={(v) => set("task_status", v)}>
                <SelectTrigger className="mt-1" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ESTATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600">Fecha de vencimiento</Label>
            {(() => { const tt = taskTypes.find((x) => x.id === form.task_type_id); return !selPrio && tt?.sla_hours ? (
              <div className="mt-1 flex items-center gap-2 p-2.5 rounded border border-amber-200 bg-amber-50" data-testid="task-due-tipo-locked">
                <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {new Date(Date.now() + tt.sla_hours * 3600000).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="text-[11px] text-amber-700">Calculada por el SLA del tipo "{tt.name}" ({tt.sla_hours}h) · no editable</p>
                </div>
              </div>
            ) : null; })()}
            {selPrio && (
              <div className="mt-1 flex items-center gap-2 p-2.5 rounded border border-amber-200 bg-amber-50" data-testid="task-due-sla-locked">
                <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {new Date(Date.now() + (selPrio.sla_hours || 24) * 3600000).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="text-[11px] text-amber-700">Calculada por el SLA de "{selPrio.name}" ({selPrio.sla_hours}h) · no editable</p>
                </div>
              </div>
            )}
            {!selPrio && !taskTypes.find((x) => x.id === form.task_type_id)?.sla_hours && (
              <>
                <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" data-testid="task-due-input" />
                <div className="flex gap-1.5 mt-1.5">
                  {[
                    { l: "Hoy", d: 0 }, { l: "Mañana", d: 1 }, { l: "+3 días", d: 3 }, { l: "+1 semana", d: 7 },
                  ].map((p) => (
                    <button
                      key={p.l}
                      onClick={() => { const dt = new Date(); dt.setDate(dt.getDate() + p.d); set("due_date", dt.toISOString().slice(0, 10)); }}
                      className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-slate-600"
                    >{p.l}</button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Repetir */}
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-slate-600">Repetir</Label>
            <Switch checked={form.repeat_enabled} onCheckedChange={(v) => set("repeat_enabled", v)} data-testid="task-repeat-switch" />
          </div>
          {form.repeat_enabled && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">cada</span>
              <Input type="number" min={1} value={form.repeat_interval} onChange={(e) => set("repeat_interval", parseInt(e.target.value) || 1)} className="w-16 h-8" />
              <Select value={form.repeat_unit} onValueChange={(v) => set("repeat_unit", v)}>
                <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dia">día(s)</SelectItem>
                  <SelectItem value="semana">semana(s)</SelectItem>
                  <SelectItem value="mes">mes(es)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-medium text-slate-600">Recordatorio</Label>
            <Select value={form.reminder} onValueChange={(v) => set("reminder", v)}>
              <SelectTrigger className="mt-1" data-testid="task-reminder-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {RECORDATORIOS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600">Notas</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Detalles, instrucciones…" className="mt-1" data-testid="task-notes-input" />
          </div>

          {/* Adjuntos con drag & drop (múltiples) */}
          <div>
            <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5" /> Adjuntos
            </Label>
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
              className={`mt-1 flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg py-5 px-3 cursor-pointer transition-colors ${
                dragOver ? "border-[#80BC00] bg-[#80BC00]/5" : "border-slate-300 hover:border-[#193966] hover:bg-slate-50"
              }`}
              data-testid="task-attach-dropzone"
            >
              <UploadCloud className={`w-6 h-6 ${dragOver ? "text-[#80BC00]" : "text-slate-400"}`} />
              <p className="text-xs text-slate-500 text-center">
                Arrastra archivos aquí o <span className="text-[#193966] font-medium">haz clic para seleccionar</span>
              </p>
              <p className="text-[10px] text-slate-400">Múltiples archivos · máx. 10 MB c/u</p>
              <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="task-attach-input" />
            </label>
            {files.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="task-attach-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded border bg-slate-50 text-sm" data-testid={`task-attach-item-${i}`}>
                    <FileText className="w-4 h-4 text-[#193966] flex-shrink-0" />
                    <span className="flex-1 truncate text-slate-700">{f.name}</span>
                    <span className="text-[10px] text-slate-400">{fmtSize(f.size)}</span>
                    <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-rose-500" data-testid={`task-attach-remove-${i}`}><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ticket en Mesa de Ayuda */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-sky-200 bg-sky-50">
            <div className="flex items-center gap-2">
              <Ticket className="w-4 h-4 text-sky-600" />
              <div>
                <p className="text-sm font-medium text-sky-800">Crear ticket en Mesa de Ayuda</p>
                <p className="text-[11px] text-sky-600">Se creará un ticket referenciando este pipeline.</p>
              </div>
            </div>
            <Switch checked={form.create_ticket} onCheckedChange={(v) => set("create_ticket", v)} data-testid="task-create-ticket-switch" />
          </div>
        </div>

        <div className="px-5 py-4 border-t sticky bottom-0 bg-white flex items-center gap-2">
          <Button onClick={() => submit(false)} disabled={saving} className="bg-[#193966] hover:bg-[#122a4d] text-white" data-testid="task-create-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}
          </Button>
          <Button onClick={() => submit(true)} disabled={saving} variant="outline" data-testid="task-create-another-btn">
            Crear y agregar otra
          </Button>
          <Button onClick={() => onOpenChange(false)} variant="ghost" className="ml-auto" data-testid="task-cancel-btn">
            Cancelar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
